/**
 * Plain JavaScript text input popup
 */

import { t } from '../i18n';
export interface TextInputPopupHandle {
    show(
        title: string,
        onConfirm: (text: string) => void,
        defaultValue?: string,
        options?: { modal?: boolean }
    ): void;
    hide(): void;
    destroy(): void;
}

export function createTextInputPopup(): TextInputPopupHandle {
    let currentOnConfirm: ((text: string) => void) | null = null;

    // Create overlay
    const overlay = document.createElement('div');
    overlay.className = 'text-input-popup-overlay';
    Object.assign(overlay.style, {
        position: 'fixed',
        top: '0',
        left: '0',
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'none',
        justifyContent: 'center',
        alignItems: 'center',
        // Must be above window-container popups (z-index 5000 in common-window.css)
        zIndex: '7000',
    });

    // Create container
    const container = document.createElement('div');
    Object.assign(container.style, {
        backgroundColor: '#1a1f2e',
        border: '1px solid #3a4354',
        borderRadius: '8px',
        padding: '20px',
        width: '300px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        color: '#e0e7ff',
        pointerEvents: 'auto',
    });

    // Title
    const titleEl = document.createElement('h3');
    Object.assign(titleEl.style, {
        margin: '0',
        fontSize: '1.2rem',
        textAlign: 'center',
    });

    // Input
    const input = document.createElement('input');
    input.type = 'text';
    Object.assign(input.style, {
        padding: '8px',
        borderRadius: '4px',
        border: '1px solid #3a4354',
        backgroundColor: '#252c3a',
        color: '#fff',
        fontSize: '1rem',
    });

    // Buttons container
    const buttonsDiv = document.createElement('div');
    Object.assign(buttonsDiv.style, {
        display: 'flex',
        justifyContent: 'space-between',
        gap: '10px',
    });

    // Cancel button
    const cancelBtn = document.createElement('button');
    cancelBtn.textContent = t('textInput.cancel');
    cancelBtn.type = 'button';
    Object.assign(cancelBtn.style, {
        flex: '1',
        padding: '8px',
        backgroundColor: '#4b5563',
        color: '#fff',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
    });

    // Confirm button
    const confirmBtn = document.createElement('button');
    confirmBtn.textContent = t('textInput.confirm');
    confirmBtn.type = 'button';
    Object.assign(confirmBtn.style, {
        flex: '1',
        padding: '8px',
        backgroundColor: '#3b82f6',
        color: '#fff',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontWeight: 'bold',
    });

    buttonsDiv.appendChild(cancelBtn);
    buttonsDiv.appendChild(confirmBtn);

    container.appendChild(titleEl);
    container.appendChild(input);
    container.appendChild(buttonsDiv);
    overlay.appendChild(container);
    document.body.appendChild(overlay);

    function hide(): void {
        overlay.style.display = 'none';
        currentOnConfirm = null;
    }

    function handleConfirm(): void {
        const value = input.value.trim();
        if (value && currentOnConfirm) {
            currentOnConfirm(value);
        }
        hide();
    }

    cancelBtn.addEventListener('click', hide);
    confirmBtn.addEventListener('click', handleConfirm);

    input.addEventListener('keydown', (e) => {
        e.stopPropagation();
        if (e.key === 'Enter') {
            handleConfirm();
        } else if (e.key === 'Escape') {
            hide();
        }
    });

    return {
        show(
            title: string,
            onConfirm: (text: string) => void,
            defaultValue = '',
            options?: { modal?: boolean }
        ) {
            titleEl.textContent = title;
            input.value = defaultValue;
            currentOnConfirm = onConfirm;
            const modal = options?.modal !== false;
            overlay.style.backgroundColor = modal ? 'rgba(0, 0, 0, 0.5)' : 'transparent';
            overlay.style.pointerEvents = modal ? 'auto' : 'none';
            overlay.style.display = 'flex';
            input.focus();
            input.select();
        },
        hide,
        destroy() {
            overlay.remove();
        },
    };
}
